/**
 * Turnstile for the iOS shell, solved in a page the shell frames.
 *
 * Turnstile only works in documents served over http(s). The iOS shell serves the
 * bundle from `capacitor://localhost` — WKWebView will not hand http/https to a custom
 * handler, so Capacitor cannot switch it — and there the widget renders but every
 * challenge fails with 600010. So the shell frames the member app's `turnstile.html`
 * from its hosted origin, a hostname the widget and the API already allow, and that page
 * posts the token back.
 *
 * Both halves of the exchange live here: `runTurnstileBridge` inside the framed page,
 * `readBridgeMessage` in `Turnstile` on the shell's side. The module is also exported
 * on its own subpath (`@elyxeer/ui/turnstile-bridge`) so the bridge page can import it
 * without pulling React and MUI in through the package root.
 */

import { loadTurnstileScript, renderTurnstile } from "./turnstile-script";

/**
 * The frames allowed to receive a token: the iOS shell. `frame-ancestors` for
 * `/turnstile.html` in the member app's `firebase.json` and `firebase.dev.json` must
 * list the same origins, or the browser refuses to frame the page at all.
 */
export const TURNSTILE_BRIDGE_PARENT_ORIGINS = [
  "capacitor://localhost",
] as const;

const MESSAGE_TYPE = "elyxeer:turnstile";

interface BridgeMessage {
  type: typeof MESSAGE_TYPE;
  token: string | null;
}

/** The token a bridge message carries, or `undefined` when `data` is not one. */
export function readBridgeMessage(
  data: unknown,
): { token: string | null } | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const message = data as Partial<BridgeMessage>;
  if (message.type !== MESSAGE_TYPE) return undefined;
  return { token: typeof message.token === "string" ? message.token : null };
}

/**
 * Render the widget into `container` and report every token change to the shell.
 *
 * The theme arrives as `?theme=dark` because the framed page cannot read the shell's
 * colour scheme. `target` is the window to report to — the parent frame in the page,
 * a stand-in in tests.
 */
export function runTurnstileBridge(
  container: HTMLElement,
  {
    search = window.location.search,
    target = window.parent,
  }: { search?: string; target?: Pick<Window, "postMessage"> } = {},
): void {
  const theme =
    new URLSearchParams(search).get("theme") === "dark" ? "dark" : "light";
  document.documentElement.style.colorScheme = theme;

  const report = (token: string | null) => {
    const message: BridgeMessage = { type: MESSAGE_TYPE, token };
    // An exact origin, never "*": the token is a credential for `otp/request`.
    for (const origin of TURNSTILE_BRIDGE_PARENT_ORIGINS) {
      target.postMessage(message, origin);
    }
  };

  void loadTurnstileScript()
    .then(() => renderTurnstile(container, { theme, onToken: report }))
    .catch(() => report(null));
}
